import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import { GPXData, AidStation, RouteSegment } from '../types';
import 'leaflet/dist/leaflet.css';
import L, { LatLngExpression } from 'leaflet';
import 'leaflet-polylinedecorator'; // Import the plugin

// Fix for default marker icons in Leaflet with React
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom icons for start and finish points
const startIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const finishIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Add a new icon for the hover marker
const hoverIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png', // Blue marker
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = defaultIcon;

// Helper function to swap [lon, lat] to [lat, lon] for Leaflet
const mapCoordsForLeaflet = (coords: [number, number, number?][] | undefined): LatLngExpression[] => {
  if (!coords) return [];
  return coords.map(c => [c[1], c[0]] as LatLngExpression); // Swap to [lat, lon]
};

// Helper for single point
const mapSingleCoordForLeaflet = (coord: [number, number, number?] | undefined): LatLngExpression | undefined => {
    if (!coord) return undefined;
    return [coord[1], coord[0]] as LatLngExpression; // Swap to [lat, lon]
};

// Define type for hovered location prop
type HoveredLocation = [number, number] | null;

interface MapViewProps {
  gpxData: GPXData | null;
  aidStations: AidStation[];
  segments: RouteSegment[];
  onAddWaypoint?: (waypoint: AidStation) => void;
  hoveredLocation: HoveredLocation; // Add the hovered location prop
}

// Component to handle initial map centering
const MapInitializer: React.FC<{ gpxData: GPXData | null }> = ({ gpxData }) => {
  const map = useMap();

  useEffect(() => {
    console.log('[MapInitializer] Running effect. gpxData exists:', !!gpxData);
    if (gpxData && gpxData.coordinates.length > 0) {
      const leafletCoords = mapCoordsForLeaflet(gpxData.coordinates);
      console.log('[MapInitializer] Leaflet coords count:', leafletCoords.length);
      if (leafletCoords.length > 0) {
          const bounds = L.latLngBounds(leafletCoords);
          console.log('[MapInitializer] Calculated bounds:', bounds);
          map.flyToBounds(bounds, {
            padding: [50, 50],
            duration: 1 // Short duration
          });
          // Force map size invalidation shortly after setting bounds
          setTimeout(() => {
            console.log('[MapInitializer] Invalidating map size...');
            map.invalidateSize();
          }, 500); // Increased delay to 500ms
      }
    }
  }, [gpxData, map]);

  return null;
};

// Component to add Polyline Decorator
const PolylineDecorator: React.FC<{ positions: LatLngExpression[] }> = ({ positions }) => {
    const map = useMap();

    useEffect(() => {
        if (positions.length === 0) return;

        // Cast positions to any to bypass type mismatch with decorator definition
        const decorator = L.polylineDecorator(positions as any, {
            patterns: [
                {
                    offset: '10%', 
                    repeat: '100px', 
                    symbol: L.Symbol.arrowHead({
                        pixelSize: 10,
                        polygon: false,
                        pathOptions: { 
                            stroke: true, 
                            color: '#0000FF', 
                            weight: 2, 
                            opacity: 0.8 
                        }
                    })
                }
            ]
        }).addTo(map);

        return () => {
            map.removeLayer(decorator);
        };
    }, [map, positions]);

    return null; // This component doesn't render anything itself
};

const MapView: React.FC<MapViewProps> = ({ gpxData, aidStations, segments, onAddWaypoint, hoveredLocation }) => {
  // Use the first coordinate as the initial center if available, mapping it correctly
  const firstCoordMapped = mapSingleCoordForLeaflet(gpxData?.coordinates[0]);
  const initialCenter: LatLngExpression = firstCoordMapped || [-8.11, -78.05]; // Default center if no data
  const initialZoom = gpxData ? 12 : 8;

  // Define a list of distinct colors for segments
  const segmentColors = [
    '#e6194B', '#3cb44b', '#ffe119', '#4363d8', '#f58231', 
    '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe',
    '#008080', '#e6beff', '#9A6324', '#fffac8', '#800000',
    '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080' 
  ];

  const handleSegmentClick = (e: L.LeafletMouseEvent, segment: RouteSegment) => {
    console.log('Segment clicked', e, segment);
    if (!onAddWaypoint || !gpxData) {
      console.log('Missing onAddWaypoint or gpxData', { onAddWaypoint, gpxData });
      return;
    }

    const clickPoint = e.latlng;
    console.log('Click point', clickPoint);
    let closestPoint: L.LatLng | null = null;
    let minDistance = Infinity;
    let closestIndex = -1;

    for (let i = 0; i < segment.coordinates.length - 1; i++) {
        // Map coordinates for Leaflet L.latLng
        const startMapped = mapSingleCoordForLeaflet(segment.coordinates[i]);
        const endMapped = mapSingleCoordForLeaflet(segment.coordinates[i + 1]);

        if (startMapped && endMapped) {
            const start = L.latLng(startMapped);
            const end = L.latLng(endMapped);
            
            const point = findClosestPointOnLine(clickPoint, start, end);
            const distance = point.distanceTo(clickPoint);
            
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = point;
              closestIndex = i;
            }
        }
    }

    console.log('Closest point found', closestPoint);

    if (closestPoint) {
      let segmentDistance = 0;
      for (let i = 0; i <= closestIndex; i++) {
        const startMapped = mapSingleCoordForLeaflet(segment.coordinates[i]);
        const endMapped = (i < closestIndex && segment.coordinates[i+1]) 
            ? mapSingleCoordForLeaflet(segment.coordinates[i + 1])
            : closestPoint; // Use the actual closest point LatLng for the last segment piece

        if (startMapped && endMapped) {
            const start = L.latLng(startMapped);
            // Ensure endMapped is LatLngExpression or LatLng
            const end = (endMapped instanceof L.LatLng) ? endMapped : L.latLng(endMapped as LatLngExpression);
            segmentDistance += start.distanceTo(end);
        }
      }
      
      const totalDistance = segment.startDistance + (segmentDistance / 1000);
      console.log('Distance calculated', totalDistance);

      const newWaypoint: AidStation = {
        id: `waypoint-${Date.now()}`,
        name: `Waypoint at ${totalDistance.toFixed(2)}km`,
        distance: totalDistance,
        location: [closestPoint.lat, closestPoint.lng],
        pcNumber: 0  // Use 0 for manually added waypoints
      };
      
      console.log('Adding new waypoint', newWaypoint);
      onAddWaypoint(newWaypoint);
    }
  };

  const findClosestPointOnLine = (point: L.LatLng, lineStart: L.LatLng, lineEnd: L.LatLng): L.LatLng => {
    const x = point.lat;
    const y = point.lng;
    const x1 = lineStart.lat;
    const y1 = lineStart.lng;
    const x2 = lineEnd.lat;
    const y2 = lineEnd.lng;

    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    let param = -1;

    if (len_sq !== 0) {
      param = dot / len_sq;
    }

    let xx, yy;

    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    return L.latLng(xx, yy);
  };

  // Map coordinates for rendering
  const gpxPositions = useMemo(() => mapCoordsForLeaflet(gpxData?.coordinates), [gpxData?.coordinates]);
  const startPosition = useMemo(() => mapSingleCoordForLeaflet(gpxData?.coordinates[0]), [gpxData?.coordinates]);
  const endPosition = useMemo(() => mapSingleCoordForLeaflet(gpxData?.coordinates[gpxData?.coordinates.length - 1]), [gpxData?.coordinates]);

  // Calculate kilometer marker positions
  const kmMarkers = useMemo(() => {
    if (!gpxData || !gpxData.distances || gpxData.distances.length === 0) {
        return [];
    }
    const markers: { pos: LatLngExpression; km: number }[] = [];
    let nextKmTarget = 1000; // Target distance in meters

    for (let i = 1; i < gpxData.distances.length; i++) {
        // Check if the distance crosses the next km mark
        if (gpxData.distances[i-1] < nextKmTarget && gpxData.distances[i] >= nextKmTarget) {
            // Found a point just after the km mark
            const pos = mapSingleCoordForLeaflet(gpxData.coordinates[i]);
            if (pos) {
                markers.push({ pos, km: nextKmTarget / 1000 });
            }
            nextKmTarget += 1000; // Set target for the next kilometer
        }
    }
    return markers;
  }, [gpxData]);

  return (
    <div className="map-container">
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        preferCanvas={true}
      >
        <MapInitializer gpxData={gpxData} /> 
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* Main GPX track - use mapped positions */}
        {gpxPositions.length > 0 && (
          <Polyline
            positions={gpxPositions}
            color="blue"
            weight={3}
            opacity={0.7}
          />
        )}

        {/* Direction Arrows */} 
        {gpxPositions.length > 0 && <PolylineDecorator positions={gpxPositions} />}

        {/* Start point marker - use mapped position */}
        {startPosition && (
          <Marker 
            position={startPosition} 
            icon={startIcon}
          >
            <Popup>
              <div>
                <strong>Start Point</strong>
                <br />
                <small>Distance: 0 km</small>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Finish point marker - use mapped position */}
        {endPosition && (
          <Marker 
            position={endPosition} 
            icon={finishIcon}
          >
            <Popup>
              <div>
                <strong>Finish Point</strong>
                <br />
                <small>Distance: {gpxData?.totalDistance?.toFixed(2) || 0} km</small>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Route segments - use mapped positions */}
        {segments.map((segment, index) => {
          const segmentPositions = mapCoordsForLeaflet(segment.coordinates);
          if (segmentPositions.length === 0) return null; // Don't render empty segments

          return (
            <React.Fragment key={segment.id}>
              {/* Visible route line */}
              <Polyline
                positions={segmentPositions}
                color={segmentColors[index % segmentColors.length]}
                weight={6}
                opacity={0.8}
                dashArray={index % 2 === 0 ? undefined : '10, 10'}
              />

              {/* Clickable area for waypoints */}
              <Polyline
                positions={segmentPositions}
                color="transparent"
                weight={30}
                opacity={0}
                eventHandlers={{
                  click: (e) => handleSegmentClick(e, segment),
                  mouseover: (e) => {
                    const element = e.target.getElement();
                    if (element) {
                      element.style.cursor = 'pointer';
                    }
                  },
                  mouseout: (e) => {
                    const element = e.target.getElement();
                    if (element) {
                      element.style.cursor = '';
                    }
                  },
                }}
              />
            </React.Fragment>
          )
        })}

        {/* Aid Station Markers */}
        {aidStations.map(station => {
          const stationPos = station.location ? mapSingleCoordForLeaflet(station.location as [number, number, number?]) : undefined;
          if (!stationPos) return null; // Don't render if location is missing
          
          return (
            <Marker key={station.id} position={stationPos}>
              <Popup>
                <div>
                  <strong>{station.name}</strong>
                  <br />
                  <small>Distance: {station.distance.toFixed(2)} km</small>
                  {station.pcNumber > 0 && <small> (PC{station.pcNumber})</small>}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Kilometer Markers */} 
        {kmMarkers.map(marker => (
            <Marker 
                key={`km-${marker.km}`}
                position={marker.pos}
                icon={createKmIcon(marker.km)}
                zIndexOffset={500} // Lower than hover marker but above lines
            >
                <Popup>{`${marker.km} km`}</Popup>
            </Marker>
        ))}

        {/* Marker for hovered location from chart */}
        {hoveredLocation && (
          <Marker 
            position={hoveredLocation} // hoveredLocation is already [lat, lon]
            icon={hoverIcon} 
            zIndexOffset={1000} // Ensure it's on top
          />
        )}

      </MapContainer>
    </div>
  );
};

// Create divIcon for kilometer markers
const createKmIcon = (km: number) => L.divIcon({
    html: `<div class="km-marker">${km}</div>`,
    className: 'km-marker-container',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
});

export default MapView; 